import { createContext, useContext, useCallback, useState, useEffect, useRef, type ReactNode } from 'react';
import { vaultApi, CipherType, FieldType, type VaultSyncResponse, type VaultLoginResponse, type VaultTwoFactorResponse } from '@/api/vault';
import {
  deriveMasterKey,
  computeMasterPasswordHash,
  stretchKey,
  decryptEncryptedKey,
  decryptCipher,
  decryptPrivateKey,
  decryptOrgKey,
  encryptCipherString,
  type SymmetricCryptoKey,
  type DecryptedVaultItem,
} from '@/lib/vault-crypto';
import {
  getBiometricDiagnostics,
  createBiometricCredential,
  authenticateWithBiometric,
  type BiometricDiagnostics,
} from '@/lib/webauthn';
import {
  saveBiometricData,
  retrievePassword,
  hasBiometricData,
  getCredentialId,
  getStoredEmail,
  clearBiometricData,
} from '@/lib/biometric-storage';

// Session timeout: 5 minutes
const SESSION_TIMEOUT_MS = 5 * 60 * 1000;

// LocalStorage key for "remember device" token
const REMEMBER_TOKEN_KEY = 'vault_remember_token';

// Helper to check if response is 2FA required
function isTwoFactorResponse(
  response: VaultLoginResponse | VaultTwoFactorResponse,
): response is VaultTwoFactorResponse {
  return 'twoFactorRequired' in response && response.twoFactorRequired === true;
}

// Pending credentials for 2FA flow
interface PendingCredentials {
  email: string;
  masterPasswordHash: string;
  masterKey: ArrayBuffer;
  stretchedKey: SymmetricCryptoKey;
}

// Parse client ID from collection name like "АлеСан-М [693]"
// Returns null if pattern not found or multiple brackets exist
function parseClientIdFromName(name: string): number | null {
  // Check for multiple [ which would be ambiguous
  const bracketCount = (name.match(/\[/g) || []).length;
  if (bracketCount !== 1) return null;

  const match = name.match(/\[(\d+)\]$/);
  if (!match) return null;

  return parseInt(match[1], 10);
}

interface VaultCollection {
  id: string;
  name: string;
  clientId: number | null;
  organizationId: string;
}

interface VaultOrganization {
  id: string;
  name: string;
  key: SymmetricCryptoKey;
}

interface VaultState {
  isUnlocked: boolean;
  isLoading: boolean;
  error: string | null;
  email: string | null;
  accessToken: string | null;
  decryptedItems: DecryptedVaultItem[];
  collections: VaultCollection[];
  organizations: VaultOrganization[];
  expiresAt: number | null;
  // 2FA state
  requiresTwoFactor: boolean;
  twoFactorProviders: number[];
  pendingCredentials: PendingCredentials | null;
}

interface CreateItemData {
  type: CipherType;
  name: string;
  notes?: string;
  // Login fields
  username?: string;
  password?: string;
  totp?: string;
  uris?: string[];
  // Card fields
  card?: {
    cardholderName?: string;
    brand?: string;
    number?: string;
    expMonth?: string;
    expYear?: string;
    code?: string;
  };
  // Identity fields
  identity?: {
    title?: string;
    firstName?: string;
    middleName?: string;
    lastName?: string;
    username?: string;
    company?: string;
    ssn?: string;
    passportNumber?: string;
    licenseNumber?: string;
    email?: string;
    phone?: string;
    address1?: string;
    address2?: string;
    address3?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
  // Custom fields
  fields?: { type: FieldType; name: string; value: string }[];
}

interface UpdateItemData {
  name: string;
  notes?: string;
  // Login fields
  username?: string;
  password?: string;
  totp?: string;
  uris?: string[];
  // Card fields
  card?: {
    cardholderName?: string;
    brand?: string;
    number?: string;
    expMonth?: string;
    expYear?: string;
    code?: string;
  };
  // Identity fields
  identity?: {
    title?: string;
    firstName?: string;
    middleName?: string;
    lastName?: string;
    username?: string;
    company?: string;
    ssn?: string;
    passportNumber?: string;
    licenseNumber?: string;
    email?: string;
    phone?: string;
    address1?: string;
    address2?: string;
    address3?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
  // Custom fields
  fields?: { type: FieldType; name: string; value: string }[];
}

interface VaultContextValue extends VaultState {
  unlock: (email: string, masterPassword: string) => Promise<void>;
  lock: () => void;
  submitTwoFactor: (token: string, remember?: boolean) => Promise<void>;
  cancelTwoFactor: () => void;
  getItemsByCollection: (collectionId: string) => DecryptedVaultItem[];
  getItemsByClientId: (clientId: number) => DecryptedVaultItem[];
  findCollectionByClientId: (clientId: number) => VaultCollection | null;
  createCollectionForClient: (clientId: number, clientName: string) => Promise<VaultCollection>;
  createItemInCollection: (collectionId: string, data: CreateItemData) => Promise<DecryptedVaultItem>;
  updateItem: (item: DecryptedVaultItem, data: UpdateItemData) => Promise<DecryptedVaultItem>;
  timeRemaining: number | null;
  // Send support
  getUserKey: () => SymmetricCryptoKey | null;
  startSessionTimer: (expiresAt: number) => void;
  // Biometric unlock
  isBiometricAvailable: boolean;
  isBiometricEnabled: boolean;
  biometricEmail: string | null;
  biometricDiagnostics: BiometricDiagnostics | null;
  setupBiometric: (email: string, password: string) => Promise<void>;
  unlockWithBiometric: () => Promise<void>;
  disableBiometric: () => void;
}

const VaultContext = createContext<VaultContextValue | null>(null);

export function useVault() {
  const ctx = useContext(VaultContext);
  if (!ctx) {
    throw new Error('useVault must be used within VaultProvider');
  }
  return ctx;
}

interface VaultProviderProps {
  children: ReactNode;
}

export function VaultProvider({ children }: VaultProviderProps) {
  const [state, setState] = useState<VaultState>({
    isUnlocked: false,
    isLoading: false,
    error: null,
    email: null,
    accessToken: null,
    decryptedItems: [],
    collections: [],
    organizations: [],
    expiresAt: null,
    // 2FA state
    requiresTwoFactor: false,
    twoFactorProviders: [],
    pendingCredentials: null,
  });

  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const userKeyRef = useRef<SymmetricCryptoKey | null>(null);

  // Biometric state
  const [isBiometricAvailable, setIsBiometricAvailable] = useState(false);
  const [isBiometricEnabled, setIsBiometricEnabled] = useState(false);
  const [biometricEmail, setBiometricEmail] = useState<string | null>(null);
  const [biometricDiagnostics, setBiometricDiagnostics] = useState<BiometricDiagnostics | null>(null);

  // Stable ref accessor for Send crypto (avoids re-render flicker in SendListTab)
  const getUserKey = useCallback(() => userKeyRef.current, []);

  // Lock vault
  const lock = useCallback(() => {
    setState({
      isUnlocked: false,
      isLoading: false,
      error: null,
      email: null,
      accessToken: null,
      decryptedItems: [],
      collections: [],
      organizations: [],
      expiresAt: null,
      // Reset 2FA state
      requiresTwoFactor: false,
      twoFactorProviders: [],
      pendingCredentials: null,
    });
    setTimeRemaining(null);
    userKeyRef.current = null;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Start session timer
  const startSessionTimer = useCallback(
    (expiresAt: number) => {
      // Clear existing timers
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);

      // Set timeout to lock
      const remaining = expiresAt - Date.now();
      timeoutRef.current = setTimeout(lock, remaining);

      // Update countdown every second
      intervalRef.current = setInterval(() => {
        const now = Date.now();
        if (now >= expiresAt) {
          lock();
        } else {
          setTimeRemaining(Math.ceil((expiresAt - now) / 1000));
        }
      }, 1000);

      setTimeRemaining(Math.ceil(remaining / 1000));
    },
    [lock],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // Check biometric availability on mount
  useEffect(() => {
    getBiometricDiagnostics().then((diag) => {
      setBiometricDiagnostics(diag);
      setIsBiometricAvailable(diag.platformAuthenticatorAvailable === true);
    });
    setIsBiometricEnabled(hasBiometricData());
    setBiometricEmail(getStoredEmail());
  }, []);

  // Complete unlock after successful login (shared between unlock and submitTwoFactor)
  const completeUnlock = useCallback(
    async (
      email: string,
      loginResponse: VaultLoginResponse,
      stretchedKey: SymmetricCryptoKey,
    ) => {
      // Save remember token if provided
      if (loginResponse.TwoFactorToken) {
        localStorage.setItem(REMEMBER_TOKEN_KEY, JSON.stringify({
          email,
          token: loginResponse.TwoFactorToken,
        }));
      }

      // 6. Decrypt the user's encryption key (encryptable for Send feature)
      const userKey: SymmetricCryptoKey = await decryptEncryptedKey(
        loginResponse.Key,
        stretchedKey,
        { encryptable: true },
      );
      userKeyRef.current = userKey;

      // 7. Decrypt private key for org key decryption
      const privateKey = await decryptPrivateKey(loginResponse.PrivateKey, userKey);

      // 8. Sync vault data
      const syncData: VaultSyncResponse = await vaultApi.sync(loginResponse.access_token);

      // 9. Decrypt organization keys
      const organizations: VaultOrganization[] = await Promise.all(
        (syncData.profile.organizations || []).map(async (org) => {
          const orgKey = await decryptOrgKey(org.key, privateKey);
          return {
            id: org.id,
            name: org.name,
            key: orgKey,
          };
        }),
      );
      const organizationIds = new Set(organizations.map((org) => org.id));
      const organizationCiphers = syncData.ciphers.filter((cipher) => (
        typeof cipher.organizationId === 'string' && organizationIds.has(cipher.organizationId)
      ));
      const organizationCollections = syncData.collections.filter((collection) => (
        typeof collection.organizationId === 'string' && organizationIds.has(collection.organizationId)
      ));

      // 10. Decrypt all ciphers (Login, SecureNote, Card, Identity)
      const decryptedItems = await Promise.all(
        organizationCiphers.map((cipher) => {
          let key = userKey;
          if (cipher.organizationId) {
            const org = organizations.find((o) => o.id === cipher.organizationId);
            if (org) {
              key = org.key;
            }
          }
          return decryptCipher(cipher, key);
        }),
      );

      // 11. Decrypt collection names and parse client IDs
      const collections: VaultCollection[] = await Promise.all(
        organizationCollections.map(async (col) => {
          const org = organizations.find((o) => o.id === col.organizationId);
          let name = col.name;

          if (org && col.name.includes('.')) {
            try {
              const { decryptCipherString } = await import('@/lib/vault-crypto');
              name = await decryptCipherString(col.name, org.key);
            } catch {
              // Keep encrypted name if decryption fails
            }
          }

          const clientId = parseClientIdFromName(name);
          return {
            id: col.id,
            name,
            clientId,
            organizationId: col.organizationId,
          };
        }),
      );

      const expiresAt = Date.now() + SESSION_TIMEOUT_MS;

      setState({
        isUnlocked: true,
        isLoading: false,
        error: null,
        email,
        accessToken: loginResponse.access_token,
        decryptedItems,
        collections,
        organizations,
        expiresAt,
        requiresTwoFactor: false,
        twoFactorProviders: [],
        pendingCredentials: null,
      });

      startSessionTimer(expiresAt);
    },
    [startSessionTimer],
  );

  // Unlock vault
  const unlock = useCallback(
    async (email: string, masterPassword: string) => {
      setState((s) => ({ ...s, isLoading: true, error: null, requiresTwoFactor: false }));

      try {
        // 1. Get KDF parameters
        const prelogin = await vaultApi.prelogin(email);
        const kdfIterations = prelogin.kdfIterations || 600000;

        // 2. Derive master key
        const masterKey = await deriveMasterKey(masterPassword, email, kdfIterations);

        // 3. Compute password hash for login
        const masterPasswordHash = await computeMasterPasswordHash(masterKey, masterPassword);

        // 5. Stretch master key (needed for later decryption)
        const stretchedKey = await stretchKey(masterKey);

        // Check for saved remember token
        let rememberToken: string | undefined;
        try {
          const saved = localStorage.getItem(REMEMBER_TOKEN_KEY);
          if (saved) {
            const { email: savedEmail, token } = JSON.parse(saved);
            if (savedEmail === email) {
              rememberToken = token;
            }
          }
        } catch {
          // Ignore parse errors
        }

        // 4. Login to vault (with remember token if available)
        const loginResponse = await vaultApi.login(
          email,
          masterPasswordHash,
          rememberToken, // twoFactorToken (remember bypass)
          rememberToken ? 5 : undefined, // provider 5 = Remember
          undefined,
        );

        // Check if 2FA is required
        if (isTwoFactorResponse(loginResponse)) {
          // Clear invalid remember token if we got 2FA required
          if (rememberToken) {
            localStorage.removeItem(REMEMBER_TOKEN_KEY);
          }

          // Save credentials for 2FA submission
          setState((s) => ({
            ...s,
            isLoading: false,
            requiresTwoFactor: true,
            twoFactorProviders: loginResponse.twoFactorProviders,
            pendingCredentials: {
              email,
              masterPasswordHash,
              masterKey,
              stretchedKey,
            },
          }));
          return;
        }

        // No 2FA required, complete unlock
        await completeUnlock(email, loginResponse, stretchedKey);
      } catch (error: unknown) {
        let errorMessage = 'Не удалось разблокировать хранилище';

        const axiosError = error as { response?: { status?: number }; message?: string };
        if (axiosError.response?.status === 401) {
          errorMessage = 'Неверный email или пароль';
        } else if (axiosError.response?.status === 503 || axiosError.message?.includes('unavailable')) {
          errorMessage = 'Сервис недоступен';
        } else if (error instanceof Error) {
          if (error.message.includes('401')) {
            errorMessage = 'Неверный email или пароль';
          } else {
            errorMessage = error.message;
          }
        }

        setState((s) => ({
          ...s,
          isLoading: false,
          error: errorMessage,
        }));
      }
    },
    [completeUnlock],
  );

  // Submit 2FA code
  const submitTwoFactor = useCallback(
    async (token: string, remember = false) => {
      if (!state.pendingCredentials) {
        setState((s) => ({ ...s, error: 'Нет ожидающих учетных данных' }));
        return;
      }

      setState((s) => ({ ...s, isLoading: true, error: null }));

      try {
        const { email, masterPasswordHash, stretchedKey } = state.pendingCredentials;

        // Login with 2FA token
        const loginResponse = await vaultApi.login(
          email,
          masterPasswordHash,
          token,
          0, // provider 0 = TOTP
          remember,
        );

        // Check if 2FA is still required (invalid code)
        if (isTwoFactorResponse(loginResponse)) {
          setState((s) => ({
            ...s,
            isLoading: false,
            error: 'Неверный код подтверждения',
          }));
          return;
        }

        // Success, complete unlock
        await completeUnlock(email, loginResponse, stretchedKey);
      } catch (error: unknown) {
        let errorMessage = 'Ошибка проверки кода';

        const axiosError = error as { response?: { status?: number }; message?: string };
        if (axiosError.response?.status === 401) {
          errorMessage = 'Неверный код подтверждения';
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }

        setState((s) => ({
          ...s,
          isLoading: false,
          error: errorMessage,
        }));
      }
    },
    [state.pendingCredentials, completeUnlock],
  );

  // Cancel 2FA flow
  const cancelTwoFactor = useCallback(() => {
    setState((s) => ({
      ...s,
      requiresTwoFactor: false,
      twoFactorProviders: [],
      pendingCredentials: null,
      error: null,
    }));
  }, []);

  // Setup biometric unlock (called after successful login)
  const setupBiometric = useCallback(
    async (email: string, password: string) => {
      if (!isBiometricAvailable) {
        throw new Error('Биометрия недоступна');
      }

      // Create WebAuthn credential (triggers biometric)
      const credential = await createBiometricCredential(email, email);

      // Authenticate to get authenticatorData for key derivation
      const authData = await authenticateWithBiometric(credential.credentialId);

      // Save encrypted password
      await saveBiometricData(credential.credentialId, email, password, authData);

      setIsBiometricEnabled(true);
      setBiometricEmail(email);
    },
    [isBiometricAvailable],
  );

  // Unlock vault using biometrics
  const unlockWithBiometric = useCallback(async () => {
    const credentialId = getCredentialId();
    if (!credentialId) {
      throw new Error('Биометрия не настроена');
    }

    setState((s) => ({ ...s, isLoading: true, error: null }));

    try {
      // Authenticate with biometrics
      const authData = await authenticateWithBiometric(credentialId);

      // Retrieve and decrypt password
      const credentials = await retrievePassword(authData);
      if (!credentials) {
        // Decryption failed - clear invalid data
        clearBiometricData();
        setIsBiometricEnabled(false);
        setBiometricEmail(null);
        throw new Error('Не удалось расшифровать данные. Войдите с паролем.');
      }

      // Use standard unlock flow with decrypted credentials
      await unlock(credentials.email, credentials.password);
    } catch (error) {
      // Convert technical WebAuthn errors to user-friendly messages
      let userMessage = 'Ошибка биометрии';
      if (error instanceof Error) {
        const msg = error.message.toLowerCase();
        if (msg.includes('not allowed') || msg.includes('notallowed')) {
          userMessage = 'Face ID недоступен. Войдите с паролем или отключите Face ID.';
        } else if (msg.includes('расшифровать')) {
          userMessage = 'Не удалось расшифровать данные. Войдите с паролем.';
        } else if (msg.includes('не настроена')) {
          userMessage = 'Face ID не настроен. Войдите с паролем.';
        } else {
          userMessage = error.message;
        }
      }
      setState((s) => ({
        ...s,
        isLoading: false,
        error: userMessage,
      }));
      throw error;
    }
  }, [unlock]);

  // Disable biometric unlock
  const disableBiometric = useCallback(() => {
    clearBiometricData();
    setIsBiometricEnabled(false);
    setBiometricEmail(null);
  }, []);

  // Get items by collection ID
  const getItemsByCollection = useCallback(
    (collectionId: string) => {
      return state.decryptedItems.filter((item) => item.collectionIds.includes(collectionId));
    },
    [state.decryptedItems],
  );

  // Find collection by client ID (from naming pattern "[id]")
  const findCollectionByClientId = useCallback(
    (clientId: number): VaultCollection | null => {
      return state.collections.find((col) => col.clientId === clientId) || null;
    },
    [state.collections],
  );

  // Get items by client ID (finds collection first, then items)
  const getItemsByClientId = useCallback(
    (clientId: number): DecryptedVaultItem[] => {
      const collection = state.collections.find((col) => col.clientId === clientId);
      if (!collection) return [];
      return state.decryptedItems.filter((item) => item.collectionIds.includes(collection.id));
    },
    [state.collections, state.decryptedItems],
  );

  // Create a new collection for a client
  const createCollectionForClient = useCallback(
    async (clientId: number, clientName: string): Promise<VaultCollection> => {
      if (!state.accessToken) {
        throw new Error('Vault not unlocked');
      }

      // Get the first organization (we assume single org setup)
      const org = state.organizations[0];
      if (!org) {
        throw new Error('No organization found');
      }

      // Create collection name with client ID pattern
      const collectionName = `${clientName} [${clientId}]`;

      // Encrypt the name with org key
      const encryptedName = await encryptCipherString(collectionName, org.key);

      // Create collection via API
      const result = await vaultApi.createCollection(
        state.accessToken,
        org.id,
        encryptedName,
      );

      // Add to local state
      const newCollection: VaultCollection = {
        id: result.id,
        name: collectionName,
        clientId,
        organizationId: org.id,
      };

      setState((s) => ({
        ...s,
        collections: [...s.collections, newCollection],
      }));

      return newCollection;
    },
    [state.accessToken, state.organizations],
  );

  // Create a new item in a collection (supports all cipher types)
  const createItemInCollection = useCallback(
    async (collectionId: string, data: CreateItemData): Promise<DecryptedVaultItem> => {
      if (!state.accessToken) {
        throw new Error('Vault not unlocked');
      }

      // Find collection to get organizationId
      const collection = state.collections.find((c) => c.id === collectionId);
      if (!collection) {
        throw new Error('Collection not found');
      }

      // Find org key
      const org = state.organizations.find((o) => o.id === collection.organizationId);
      if (!org) {
        throw new Error('Organization not found');
      }

      // Encrypt common fields
      const encryptedName = await encryptCipherString(data.name, org.key);
      const encryptedNotes = data.notes
        ? await encryptCipherString(data.notes, org.key)
        : undefined;

      // Build encrypted data based on type
      let encryptedLogin;
      let encryptedCard;
      let encryptedIdentity;

      if (data.type === CipherType.Login) {
        const encryptedUsername = data.username
          ? await encryptCipherString(data.username, org.key)
          : undefined;
        const encryptedPassword = data.password
          ? await encryptCipherString(data.password, org.key)
          : undefined;
        const encryptedTotp = data.totp
          ? await encryptCipherString(data.totp, org.key)
          : undefined;
        const encryptedUris = data.uris
          ? await Promise.all(
              data.uris.map(async (uri) => ({
                uri: await encryptCipherString(uri, org.key),
              })),
            )
          : undefined;

        encryptedLogin = {
          username: encryptedUsername,
          password: encryptedPassword,
          totp: encryptedTotp,
          uris: encryptedUris,
        };
      }

      if (data.type === CipherType.Card && data.card) {
        encryptedCard = {
          cardholderName: data.card.cardholderName
            ? await encryptCipherString(data.card.cardholderName, org.key)
            : undefined,
          brand: data.card.brand
            ? await encryptCipherString(data.card.brand, org.key)
            : undefined,
          number: data.card.number
            ? await encryptCipherString(data.card.number, org.key)
            : undefined,
          expMonth: data.card.expMonth
            ? await encryptCipherString(data.card.expMonth, org.key)
            : undefined,
          expYear: data.card.expYear
            ? await encryptCipherString(data.card.expYear, org.key)
            : undefined,
          code: data.card.code
            ? await encryptCipherString(data.card.code, org.key)
            : undefined,
        };
      }

      if (data.type === CipherType.Identity && data.identity) {
        const id = data.identity;
        encryptedIdentity = {
          title: id.title ? await encryptCipherString(id.title, org.key) : undefined,
          firstName: id.firstName ? await encryptCipherString(id.firstName, org.key) : undefined,
          middleName: id.middleName ? await encryptCipherString(id.middleName, org.key) : undefined,
          lastName: id.lastName ? await encryptCipherString(id.lastName, org.key) : undefined,
          username: id.username ? await encryptCipherString(id.username, org.key) : undefined,
          company: id.company ? await encryptCipherString(id.company, org.key) : undefined,
          ssn: id.ssn ? await encryptCipherString(id.ssn, org.key) : undefined,
          passportNumber: id.passportNumber ? await encryptCipherString(id.passportNumber, org.key) : undefined,
          licenseNumber: id.licenseNumber ? await encryptCipherString(id.licenseNumber, org.key) : undefined,
          email: id.email ? await encryptCipherString(id.email, org.key) : undefined,
          phone: id.phone ? await encryptCipherString(id.phone, org.key) : undefined,
          address1: id.address1 ? await encryptCipherString(id.address1, org.key) : undefined,
          address2: id.address2 ? await encryptCipherString(id.address2, org.key) : undefined,
          address3: id.address3 ? await encryptCipherString(id.address3, org.key) : undefined,
          city: id.city ? await encryptCipherString(id.city, org.key) : undefined,
          state: id.state ? await encryptCipherString(id.state, org.key) : undefined,
          postalCode: id.postalCode ? await encryptCipherString(id.postalCode, org.key) : undefined,
          country: id.country ? await encryptCipherString(id.country, org.key) : undefined,
        };
      }

      // Encrypt custom fields
      const encryptedFields = data.fields
        ? await Promise.all(
            data.fields.map(async (f) => ({
              type: f.type,
              name: await encryptCipherString(f.name, org.key),
              value: await encryptCipherString(f.value, org.key),
            })),
          )
        : undefined;

      // Create cipher via API
      const result = await vaultApi.createCipher(state.accessToken, {
        type: data.type,
        organizationId: collection.organizationId,
        collectionIds: [collectionId],
        name: encryptedName,
        notes: encryptedNotes,
        login: encryptedLogin,
        card: encryptedCard,
        identity: encryptedIdentity,
        fields: encryptedFields,
      });

      // Create decrypted item for local state
      const newItem: DecryptedVaultItem = {
        id: result.id,
        type: data.type,
        name: data.name,
        notes: data.notes,
        username: data.username,
        password: data.password,
        totp: data.totp,
        uris: data.uris,
        card: data.card,
        identity: data.identity,
        fields: data.fields,
        collectionIds: [collectionId],
      };

      setState((s) => ({
        ...s,
        decryptedItems: [...s.decryptedItems, newItem],
      }));

      return newItem;
    },
    [state.accessToken, state.collections, state.organizations],
  );

  // Update an existing item
  const updateItem = useCallback(
    async (item: DecryptedVaultItem, data: UpdateItemData): Promise<DecryptedVaultItem> => {
      if (!state.accessToken) {
        throw new Error('Vault not unlocked');
      }

      // Find collection to get organizationId
      const collectionId = item.collectionIds[0];
      const collection = state.collections.find((c) => c.id === collectionId);
      if (!collection) {
        throw new Error('Collection not found');
      }

      // Find org key
      const org = state.organizations.find((o) => o.id === collection.organizationId);
      if (!org) {
        throw new Error('Organization not found');
      }

      // Encrypt all fields
      const encryptedName = await encryptCipherString(data.name, org.key);
      const encryptedNotes = data.notes
        ? await encryptCipherString(data.notes, org.key)
        : undefined;

      // Build encrypted data based on item type
      let encryptedLogin;
      let encryptedCard;
      let encryptedIdentity;

      if (item.type === CipherType.Login) {
        const encryptedUsername = data.username
          ? await encryptCipherString(data.username, org.key)
          : undefined;
        const encryptedPassword = data.password
          ? await encryptCipherString(data.password, org.key)
          : undefined;
        const encryptedTotp = data.totp
          ? await encryptCipherString(data.totp, org.key)
          : undefined;
        const encryptedUris = data.uris
          ? await Promise.all(
              data.uris.map(async (uri) => ({
                uri: await encryptCipherString(uri, org.key),
              })),
            )
          : undefined;

        encryptedLogin = {
          username: encryptedUsername,
          password: encryptedPassword,
          totp: encryptedTotp,
          uris: encryptedUris,
        };
      }

      if (item.type === CipherType.Card && data.card) {
        encryptedCard = {
          cardholderName: data.card.cardholderName
            ? await encryptCipherString(data.card.cardholderName, org.key)
            : undefined,
          brand: data.card.brand
            ? await encryptCipherString(data.card.brand, org.key)
            : undefined,
          number: data.card.number
            ? await encryptCipherString(data.card.number, org.key)
            : undefined,
          expMonth: data.card.expMonth
            ? await encryptCipherString(data.card.expMonth, org.key)
            : undefined,
          expYear: data.card.expYear
            ? await encryptCipherString(data.card.expYear, org.key)
            : undefined,
          code: data.card.code
            ? await encryptCipherString(data.card.code, org.key)
            : undefined,
        };
      }

      if (item.type === CipherType.Identity && data.identity) {
        const id = data.identity;
        encryptedIdentity = {
          title: id.title ? await encryptCipherString(id.title, org.key) : undefined,
          firstName: id.firstName ? await encryptCipherString(id.firstName, org.key) : undefined,
          middleName: id.middleName ? await encryptCipherString(id.middleName, org.key) : undefined,
          lastName: id.lastName ? await encryptCipherString(id.lastName, org.key) : undefined,
          username: id.username ? await encryptCipherString(id.username, org.key) : undefined,
          company: id.company ? await encryptCipherString(id.company, org.key) : undefined,
          ssn: id.ssn ? await encryptCipherString(id.ssn, org.key) : undefined,
          passportNumber: id.passportNumber ? await encryptCipherString(id.passportNumber, org.key) : undefined,
          licenseNumber: id.licenseNumber ? await encryptCipherString(id.licenseNumber, org.key) : undefined,
          email: id.email ? await encryptCipherString(id.email, org.key) : undefined,
          phone: id.phone ? await encryptCipherString(id.phone, org.key) : undefined,
          address1: id.address1 ? await encryptCipherString(id.address1, org.key) : undefined,
          address2: id.address2 ? await encryptCipherString(id.address2, org.key) : undefined,
          address3: id.address3 ? await encryptCipherString(id.address3, org.key) : undefined,
          city: id.city ? await encryptCipherString(id.city, org.key) : undefined,
          state: id.state ? await encryptCipherString(id.state, org.key) : undefined,
          postalCode: id.postalCode ? await encryptCipherString(id.postalCode, org.key) : undefined,
          country: id.country ? await encryptCipherString(id.country, org.key) : undefined,
        };
      }

      // Encrypt custom fields
      const encryptedFields = data.fields
        ? await Promise.all(
            data.fields.map(async (f) => ({
              type: f.type,
              name: await encryptCipherString(f.name, org.key),
              value: await encryptCipherString(f.value, org.key),
            })),
          )
        : undefined;

      // Update cipher via API
      await vaultApi.updateCipher(state.accessToken, item.id, {
        type: item.type,
        organizationId: collection.organizationId,
        collectionIds: item.collectionIds,
        name: encryptedName,
        notes: encryptedNotes,
        login: encryptedLogin,
        card: encryptedCard,
        identity: encryptedIdentity,
        fields: encryptedFields,
      });

      // Create updated decrypted item for local state
      const updatedItem: DecryptedVaultItem = {
        ...item,
        name: data.name,
        notes: data.notes,
        username: data.username,
        password: data.password,
        totp: data.totp,
        uris: data.uris,
        card: data.card,
        identity: data.identity,
        fields: data.fields,
      };

      // Update local state
      setState((s) => ({
        ...s,
        decryptedItems: s.decryptedItems.map((i) =>
          i.id === item.id ? updatedItem : i,
        ),
      }));

      return updatedItem;
    },
    [state.accessToken, state.collections, state.organizations],
  );

  return (
    <VaultContext.Provider
      value={{
        ...state,
        unlock,
        lock,
        submitTwoFactor,
        cancelTwoFactor,
        getItemsByCollection,
        getItemsByClientId,
        findCollectionByClientId,
        createCollectionForClient,
        createItemInCollection,
        updateItem,
        timeRemaining,
        // Send support
        getUserKey,
        startSessionTimer,
        // Biometric unlock
        isBiometricAvailable,
        isBiometricEnabled,
        biometricEmail,
        biometricDiagnostics,
        setupBiometric,
        unlockWithBiometric,
        disableBiometric,
      }}
    >
      {children}
    </VaultContext.Provider>
  );
}
